import { useState } from "react";
import { ChevronDown, Link2, Plus, StickyNote, Trash2, Unlink, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CHARACTER_TYPES, SHIP_STATUSES, SHIP_TYPES, SPECIES_RELATIONS, STATUS_ORDER } from "@/lib/constants";
import { LifespanEditor, SapientSwitch, SpeciesStatusSelect } from "@/components/SpeciesFields";
import { FAMILY_ROLES } from "@/lib/family";
import { blankItem, isFilled, itemPage, newKey, presetOf } from "@/lib/infobox";

const replaceAt = (arr, i, v) => arr.map((x, j) => (j === i ? v : x));
const removeAt = (arr, i) => arr.filter((_, j) => j !== i);

const PagePicker = ({ pages, onPick, testId }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Link a page" className="p-1.5 rounded text-slate-500 hover:text-amber-400 hover:bg-amber-500/10" data-testid={`${testId}-link-btn`}><Link2 className="w-3.5 h-3.5" /></button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2 bg-panel border-line" align="end">
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages" className="h-8 mb-2" data-testid={`${testId}-picker-search`} />
        <div className="max-h-52 overflow-y-auto">
          {pages.filter((p) => p.title.toLowerCase().includes(q.toLowerCase())).map((p) => (
            <button key={p.id} type="button" onClick={() => { onPick(p); setOpen(false); setQ(""); }} data-testid={`${testId}-option-${p.id}`}
              className="block w-full text-left text-sm px-2 py-1.5 rounded text-slate-300 hover:bg-amber-500/10 hover:text-amber-200 truncate">{p.title}</button>
          ))}
          {pages.length === 0 && <p className="text-xs text-slate-500 italic px-2 py-1">No pages to link yet.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ItemRow = ({ item, onChange, onRemove, pickable, allPages, suggest, roles, testId }) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const showNote = noteOpen || !!item.note;
  const linked = itemPage(item, allPages);
  return (
    <div className="group space-y-1" data-testid={testId}>
      <div className="flex items-center gap-1">
        {linked ? (
          <span className="flex-1 min-w-0 inline-flex items-center gap-1 font-code text-xs text-amber-400 bg-amber-950/40 border border-amber-800/50 rounded px-2 py-1.5" data-testid={`${testId}-linked`}>
            <span className="truncate">{linked.title}</span>
            <button type="button" title="Unlink" onClick={() => onChange({ ...item, page_id: null, text: linked.title })} className="ml-auto text-amber-600 hover:text-amber-300" data-testid={`${testId}-unlink`}><Unlink className="w-3 h-3" /></button>
          </span>
        ) : (
          <Input value={item.text} onChange={(e) => onChange({ ...item, text: e.target.value, page_id: null })} list={suggest || undefined}
            placeholder="Value" className="h-8 text-sm bg-canvas flex-1 min-w-0" data-testid={`${testId}-input`} />
        )}
        {roles && (
          <select value={item.role || ""} onChange={(e) => onChange({ ...item, role: e.target.value || null })} title={roles.title}
            className="h-8 w-[92px] shrink-0 rounded-md bg-canvas border border-line text-[11px] text-slate-300 px-1" data-testid={`${testId}-role`}>
            <option value="">{roles.placeholder}</option>
            {roles.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
        <PagePicker pages={pickable} testId={testId} onPick={(p) => onChange({ ...item, page_id: p.id, text: p.title })} />
        <button type="button" title="Add note" onClick={() => setNoteOpen((v) => !v)} className={`p-1.5 rounded hover:bg-veil/5 ${item.note ? "text-amber-400" : "text-slate-500"}`} data-testid={`${testId}-note-toggle`}><StickyNote className="w-3.5 h-3.5" /></button>
        <button type="button" title="Remove item" onClick={onRemove} className="p-1.5 rounded text-slate-500 hover:text-rose-400 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-testid={`${testId}-remove`}><X className="w-3.5 h-3.5" /></button>
      </div>
      {showNote && (
        <Input value={item.note} maxLength={120} onChange={(e) => onChange({ ...item, note: e.target.value })} placeholder="Short note, e.g. 1864–1865, ended"
          className="h-7 text-xs bg-canvas text-slate-400" data-testid={`${testId}-note`} />
      )}
    </div>
  );
};

const LabelSelect = ({ fact, onChange, testId }) => (
  <select value={fact.items[0]?.text || "Fanon"} onChange={(e) => onChange({ ...fact, items: [{ text: e.target.value, page_id: null, note: "" }] })}
    className="w-full h-8 rounded-md bg-canvas border border-line text-sm text-slate-200 px-2" data-testid={`${testId}-status-select`}>
    {STATUS_ORDER.map((l) => <option key={l} value={l} style={{ color: SHIP_STATUSES[l].hex }}>{l}</option>)}
  </select>
);

const FactRow = ({ fact, preset, onChange, onRemove, pickable, allPages, suggest, roles, special, mirror, testId }) => (
  <div className="py-2.5 space-y-1.5" data-testid={testId}>
    <div className="flex items-center gap-2">
      {preset ? (
        <span className="font-code text-[11px] uppercase tracking-wider text-slate-400">{fact.key}</span>
      ) : (
        <Input value={fact.key} onChange={(e) => onChange({ ...fact, key: e.target.value })} placeholder="Fact name" className="h-7 text-xs font-code uppercase tracking-wider bg-transparent" data-testid={`${testId}-key`} />
      )}
      {!preset && <button type="button" onClick={onRemove} title="Delete fact" className="text-slate-500 hover:text-rose-400" data-testid={`${testId}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>}
    </div>
    {special === "mirror" && <p className="text-sm text-slate-300 px-1" data-testid={`${testId}-mirror`}>{mirror || <span className="italic text-slate-500">Mirrors the page title</span>}</p>}
    {special === "label" && <LabelSelect fact={fact} onChange={onChange} testId={testId} />}
    {special === "speciesStatus" && <SpeciesStatusSelect fact={fact} onChange={onChange} testId={testId} />}
    {special === "sapient" && <SapientSwitch fact={fact} onChange={onChange} testId={testId} />}
    {special === "lifespan" && <LifespanEditor fact={fact} onChange={onChange} testId={testId} />}
    {!special && fact.items.map((it, i) => (
      <ItemRow key={i} item={it} pickable={pickable} allPages={allPages} suggest={suggest} roles={roles} testId={`${testId}-item-${i}`}
        onChange={(v) => onChange({ ...fact, items: replaceAt(fact.items, i, v) })}
        onRemove={() => onChange({ ...fact, items: fact.items.length > 1 ? removeAt(fact.items, i) : [blankItem()] })} />
    ))}
    {!special && (
      <button type="button" onClick={() => onChange({ ...fact, items: [...fact.items, blankItem()] })} className="text-[11px] text-amber-400/80 hover:text-amber-300 flex items-center gap-1" data-testid={`${testId}-add-item`}>
        <Plus className="w-3 h-3" /> item
      </button>
    )}
  </div>
);

const LISTS = { "Characters:Character Type": "character-type-options", "Ships:Ship Type": "ship-type-options" };
const SPECIAL = { Ships: { "Ship Name": "mirror", Status: "label" }, Species: { Status: "speciesStatus", Sapient: "sapient", Lifespan: "lifespan" } };
const ROLES = {
  "Relationships:Family": { title: "Family role", placeholder: "Role…", options: FAMILY_ROLES },
  "Species Information:Related Species": { title: "Relation", placeholder: "Relation…", options: SPECIES_RELATIONS.map((r) => [r[0], r[1]]) },
};

const SectionPanel = ({ section, idx, category, onChange, onRemove, pickableFor, allPages, defaultOpen, title }) => {
  const [open, setOpen] = useState(defaultOpen);
  const preset = presetOf(category, section.title);
  const filled = section.facts.filter((f) => f.items.some(isFilled)).length;
  const testId = `infobox-section-${idx}`;
  return (
    <div className="rounded-lg border border-line bg-panel" data-testid={testId}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-slate-400" data-testid={`${testId}-toggle`}>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
        {preset ? (
          <button type="button" onClick={() => setOpen((o) => !o)} className="font-display text-lg text-amber-100 text-left flex-1 min-w-0 truncate">{preset.short}</button>
        ) : (
          <Input value={section.title} onChange={(e) => onChange({ ...section, title: e.target.value })} placeholder="Section title" className="h-7 font-display text-base bg-transparent flex-1" data-testid={`${testId}-title`} />
        )}
        <span className="font-code text-[10px] text-slate-500 shrink-0" data-testid={`${testId}-count`}>· {filled}/{section.facts.length}</span>
        {!preset && <button type="button" onClick={onRemove} title="Delete section" className="text-slate-500 hover:text-rose-400" data-testid={`${testId}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>}
      </div>
      {open && (
        <div className="px-3 pb-3 divide-y divide-line border-t border-line">
          {section.facts.map((f, i) => (
            <FactRow key={i} fact={f} preset={!!preset?.facts.includes(f.key)} pickable={pickableFor(section.title, f.key)} allPages={allPages}
              suggest={preset ? LISTS[`${category}:${f.key}`] : undefined} roles={preset ? ROLES[`${preset.title}:${f.key}`] : undefined}
              special={preset ? SPECIAL[category]?.[f.key] : undefined} mirror={title} testId={`${testId}-fact-${i}`}
              onChange={(v) => onChange({ ...section, facts: replaceAt(section.facts, i, v) })}
              onRemove={() => onChange({ ...section, facts: removeAt(section.facts, i) })} />
          ))}
          <button type="button" onClick={() => onChange({ ...section, facts: [...section.facts, { key: "", items: [blankItem()] }] })}
            className="pt-2.5 text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid={`${testId}-add-fact`}><Plus className="w-3 h-3" /> fact</button>
        </div>
      )}
    </div>
  );
};

export const InfoboxEditor = ({ sections, onChange, category, pages, selfId, title }) => {
  const others = pages.filter((p) => p.id !== selfId);
  const of = (c) => others.filter((p) => p.category === c);
  const pickableFor = (t, key) => {
    if ((category === "Characters" && t === "Relationships") || (category === "Ships" && t === "Ship Information")) return of("Characters");
    if ((category === "Characters" && t === "Biographical Information" && key === "Species") || (category === "Species" && key === "Related Species")) return of("Species");
    if (category === "Species" && t === "Species Information" && key === "Origin") return of("Locations");
    return others;
  };
  return (
    <div className="space-y-2.5" data-testid="infobox-editor">
      <datalist id="character-type-options">{CHARACTER_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
      <datalist id="ship-type-options">{SHIP_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
      {sections.map((s, i) => (
        <SectionPanel key={s._k || `${category}-${i}`} section={s} idx={i} category={category} allPages={pages} pickableFor={pickableFor} defaultOpen={i === 0} title={title}
          onChange={(v) => onChange(replaceAt(sections, i, v))} onRemove={() => onChange(removeAt(sections, i))} />
      ))}
      <button type="button" onClick={() => onChange([...sections, { _k: newKey(), title: "", facts: [{ key: "", items: [blankItem()] }] }])}
        className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="add-section-btn"><Plus className="w-3 h-3" /> section</button>
    </div>
  );
};
